// Talep 748 (#737'nin devamı) — TÜM belge ekranlarında ortak "seçili depodaki stok" bilgisi.
//
// Kural: kullanıcı bir ürünü, SEÇTİĞİ DEPODAKİ stok rakamını görmeden kaleme eklememeli;
// eklendikten sonra da satır listesinde o rakamı görebilmeli. Bu yüzden hem ürün seçme
// alanında hem de eklenmiş kalem satırlarında aynı rozet gösterilir.
//
// #737 ilk uygulamada Fatura + Sipariş + Stok Belgesi ekranlarına ayrı ayrı kopyalanmıştı;
// Talep 748 aynı davranışı diğer belge ekranlarına da istediği için mantık buraya taşındı.
// Yeni bir belge ekranı eklerken: useStockBalances(...) + <StockBadge .../> yeter.
//
// Backend: GET /api/stocks/GetBalanceByKey?VendorId&WarehouseId&ProductId
//          → { data: { available, physical } }. `available` (kullanılabilir) tercih edilir,
//          yoksa `physical` (fiziksel) fallback'tir.
import { useEffect, useState, useCallback, useRef } from 'react';
import { authHeaders } from './listPageStyles';

// #737 (2. tur) "Alan çok küçük geliyor okunmuyor. Ayrıca ... salt okunur kutu içerisinde ürün
// kod-ad alanının yanına" — rozet 11px yazı + 1px dolgulu ince bir etiketti; ekranda okunmuyordu.
// Artık salt-okunur bir ALAN gibi görünüyor: çerçeveli kutu, 12.5px tabular rakam, nefes payı.
// Yer değişmedi (ürün kod-ad'ının hemen yanı) — istenen buydu; sorun boyut ve kutu görünümüydü.
const pillStyle = (color, bg, border) => ({
  display: 'inline-block', marginLeft: 8, padding: '3px 10px', borderRadius: 8,
  fontSize: 12.5, fontWeight: 700, color, background: bg,
  border: `1px solid ${border}`,
  whiteSpace: 'nowrap', verticalAlign: 'middle',
  fontVariantNumeric: 'tabular-nums',
});

/**
 * Seçili depo+distribütör için ürün bakiyelerini (tembel, ürün başına) çeker.
 *
 * @param {string}   vendorId    Distribütör id — yoksa sorgu yapılmaz.
 * @param {string}   warehouseId Depo id — yoksa sorgu yapılmaz.
 * @param {string[]} productIds  Bakiyesi gereken ürünler (kalemler + taslak satır).
 * @returns {{ balanceOf: (productId:string) => number|null|'loading'|undefined, ready: boolean,
 *            reload: () => void }}
 *          `reload` önbelleği boşaltır → görünen ürünler için bakiye YENİDEN çekilir. Belge
 *          kaydedildikten sonra çağrılır (bkz. aşağıdaki #797/#799/#805 notu).
 */
export function useStockBalances(vendorId, warehouseId, productIds) {
  const [balances, setBalances] = useState({});
  // #737/#748 Hangi anahtar için istek AÇILDI — STATE DEĞİL, ref.
  // Sebebi aşağıdaki effect'in neden bozuk olduğuyla aynı: bu bilgi state olursa effect kendi
  // yazdığı şeyi dinler.
  const requestedRef = useRef(new Set());
  // #797 (3. tur) [reload] için TETİKLEYİCİ. Önbelleği boşaltmak tek başına yetmez: fetch effect'i
  // yalnız (vendorId, warehouseId, idsKey) değişince koşar ve kaydetme bunların HİÇBİRİNİ
  // değiştirmez. Bu sayaç bağımlılık dizisinde durur; artınca effect yeniden koşar.
  const [reloadNonce, setReloadNonce] = useState(0);

  // Depo/distribütör değişince önbellek sıfırlanır — bakiye depoya özeldir.
  useEffect(() => {
    requestedRef.current = new Set();
    setBalances({});
  }, [vendorId, warehouseId]);

  // productIds her render'da yeni dizi olabilir; efekt bağımlılığı için stabil bir anahtar.
  const idsKey = [...new Set((productIds || []).filter(Boolean))].sort().join(',');

  // #737/#748 BAKİYE HİÇ GÖRÜNMÜYORDU ("…" takılı kalıyordu) — sebebi buradaydı.
  //
  // Eskiden effect'in bağımlılıklarında `balances` vardı ve cleanup'ta `cancelled = true` yapılıyordu.
  // Effect ise ilk işi olarak `balances`'a 'loading' yazıyordu → state değişti → effect YENİDEN koştu →
  // önceki koşumun cleanup'ı çalışıp uçuşta olan isteği İPTAL etti. Cevap 200 dönse bile
  // `if (!cancelled) setBalances(...)` atlandığı için değer hiç yazılmıyor, hücre sonsuza dek 'loading'
  // kalıyordu. (Ağ sekmesinde istekler görünür ve 200'dür — bu yüzden hata backend'de sanılıyordu.)
  //
  // Çözüm: "istek açıldı mı" bilgisi ref'te tutulur (render tetiklemez) → `balances` bağımlılıktan
  // çıkar → cleanup'a da gerek kalmaz. Bileşen sökülürse setState'in etkisi yoktur (React 18).
  useEffect(() => {
    if (!vendorId || !warehouseId) return;
    const ids = idsKey ? idsKey.split(',') : [];
    const missing = ids.filter((pid) => !requestedRef.current.has(`${warehouseId}|${pid}`));
    if (missing.length === 0) return;

    missing.forEach((pid) => requestedRef.current.add(`${warehouseId}|${pid}`));
    setBalances((prev) => {
      const next = { ...prev };
      missing.forEach((pid) => { next[`${warehouseId}|${pid}`] = 'loading'; });
      return next;
    });
    missing.forEach(async (pid) => {
      const key = `${warehouseId}|${pid}`;
      try {
        const res = await fetch(
          `/api/stocks/GetBalanceByKey?VendorId=${vendorId}&WarehouseId=${warehouseId}&ProductId=${pid}`,
          { headers: authHeaders() },
        );
        const body = res.ok ? await res.json() : null;
        const d = body?.data ?? body?.Data;
        const val = d ? (d.available ?? d.physical ?? null) : null;
        setBalances((prev) => ({ ...prev, [key]: val }));
      } catch {
        setBalances((prev) => ({ ...prev, [key]: null }));
      }
    });
  }, [vendorId, warehouseId, idsKey, reloadNonce]);

  const balanceOf = useCallback(
    (productId) => (productId ? balances[`${warehouseId}|${productId}`] : undefined),
    [balances, warehouseId],
  );

  // Belge kaydedildikten SONRA sunucu bakiyesi değişmiştir; önbellek boşaltılır ve fetch effect'i
  // eksik anahtarları yeniden ister → hücreler kaydedilen belgenin etkisini içeren bakiyeye döner.
  //
  // #797 (3. tur) "Kaydet dediğimde Stok Miktar alanı '…' oluyor, refresh yapmam gerekiyor."
  // Sebep: burada YALNIZCA `requestedRef` (ref → render tetiklemez) ve `balances` sıfırlanıyordu.
  // Effect'in bağımlılıklarında bu ikisi YOK; kaydetmede vendor/depo/ürün listesi de değişmediği
  // için effect BİR DAHA HİÇ KOŞMUYORDU. Sonuç: `balances` boş → `balanceOf` `undefined` → hücre
  // kalıcı olarak "…". Kullanıcının elle F5'i bileşeni yeniden mount ettiği için "refresh yapınca
  // düzeliyor" görünüyordu. Çözüm: sıfırlamayla birlikte effect'i tetikleyen sayacı artır.
  const reload = useCallback(() => {
    requestedRef.current = new Set();
    setBalances({});
    setReloadNonce((n) => n + 1);
  }, []);

  return { balanceOf, ready: Boolean(vendorId && warehouseId), reload };
}

/*
 * #797/#799/#805 STOK ALANI TAHMİN YÜRÜTMEZ — DEFTERDE NE VARSA ONU GÖSTERİR.
 *
 * Önceki uygulama (`pendingStockDelta`) belgedeki miktarları belgenin yönüyle işaretleyip bakiyeye
 * ekliyor, böylece rakam kaydetmeden önce "düşmüş" görünüyordu. Bu üç ayrı şikâyet doğurdu:
 *
 *   • Taslak satırın miktarı varsayılan olarak 1 geldiği için, kullanıcı miktara HİÇ dokunmadan
 *     stok 10 yerine 11 (giriş belgesi) ya da 9 (çıkış belgesi) görünüyordu — #799/#805.
 *   • Kaydettikten sonra sunucu bakiyesi yeniden çekilmediğinden ekrandaki rakam ne tahminle ne
 *     gerçekle tutuyordu; belge kapatılıp açılınca "düzeliyordu" — #797.
 *   • Tahmin, sunucudaki yön matrisinin (DocumentEffects) web'e kopyalanmasına bağlıydı; iki taraf
 *     ayrışınca rakam sessizce yanlış oluyordu.
 *
 * Karar: stok hücresi yalnız KAYITLI bakiyeyi gösterir. Kullanıcı kalem girerken rakam sabit kalır;
 * belge kaydedilip defterde hareket oluşunca [useStockBalances] `reload()` ile yeniden okunur ve
 * hücre gerçek yeni bakiyeye döner. Gösterilen her rakamın arkasında bir hareket kaydı vardır.
 */

/**
 * Kalem satırında / ürün seçicisinde gösterilen "Stok: <miktar>" rozeti.
 * Pozitif bakiye yeşil, sıfır/negatif kırmızı — kullanıcı eklemeden önce görsün.
 *
 * #797.2 Bakiye TABAN birimdedir; rozet satırda SEÇİLİ birime çevrilerek gösterilir (24 adetlik stok,
 * Paket=12 seçiliyken "2", Koli=24 seçiliyken "1"). Aksi halde kullanıcı adet rakamını koli sanıyordu.
 * #797.3 Etiket "Depo" değil "Stok" — rozet deponun adını değil, o depodaki stok RAKAMINI gösteriyor.
 */
export function StockBadge({ productId, balanceOf, ready, label = 'Stok', unitMultiplier = 1 }) {
  if (!productId) return null;
  const muted = ['#64748b', 'rgba(148,163,184,0.14)', 'rgba(100,116,139,0.35)'];
  if (!ready) return <span style={pillStyle(...muted)} title="Bakiye için önce distribütör ve depo seçin">{label}: depo seçin</span>;
  const v = balanceOf(productId);
  if (v === undefined || v === 'loading') return <span style={pillStyle(...muted)}>{label}: …</span>;
  if (v == null) return <span style={pillStyle(...muted)} title="Bu depo/ürün için bakiye kaydı yok">{label}: —</span>;
  const mul = Number(unitMultiplier) > 0 ? Number(unitMultiplier) : 1;
  const baseNow = Number(v);
  const n = baseNow / mul;
  const [c, bg, br] = n > 0
    ? ['#15803d', 'rgba(22,163,74,0.12)', 'rgba(22,163,74,0.45)']
    : ['#b91c1c', 'rgba(220,38,38,0.12)', 'rgba(220,38,38,0.45)'];
  const title = mul === 1
    ? `Seçili depodaki kullanılabilir stok: ${n.toLocaleString('tr-TR')}`
    : `Seçili depodaki kullanılabilir stok: ${n.toLocaleString('tr-TR')} (seçili birim) · ${baseNow.toLocaleString('tr-TR')} taban birim`;
  return (
    <span style={pillStyle(c, bg, br)} title={title}>
      {label}: {n.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
    </span>
  );
}

/**
 * #748 (2. tur) STOK, ARTIK KENDİ KOLONUDUR.
 *
 * Rozet ürün açıklamasının yanında duruyordu; ekran görüntüsünde görüldüğü gibi o kolon zaten
 * daralınca rozet KIRPILIYOR ("Depo: …") ve renkli kutu kalem rakamlarının düzeninden ayrık
 * duruyordu. Talep: "Miktar - Birim'den hemen sonra Stok başlığı, altında rakam; format olarak
 * diğerleri ile aynı." Bu yüzden bu bileşen kutu/rozet DEĞİL, diğer sayı hücreleri gibi düz metin
 * basar. Tek istisna: bakiye eksiye düşmüşse rakam kırmızıdır — o bir uyarıdır, süs değil.
 *
 * Hesap [StockBadge] ile birebir aynıdır: kayıtlı bakiye, satırda seçili birime çevrilir.
 */
export function StockCell({ productId, balanceOf, ready, unitMultiplier = 1 }) {
  const muted = { color: '#94a3b8', fontWeight: 500 };
  if (!productId) return <span style={muted}>—</span>;
  if (!ready) return <span style={muted} title="Bakiye için önce distribütör ve depo seçin">depo seçin</span>;
  const v = balanceOf(productId);
  if (v === undefined || v === 'loading') return <span style={muted}>…</span>;
  if (v == null) return <span style={muted} title="Bu depo/ürün için bakiye kaydı yok">—</span>;
  const mul = Number(unitMultiplier) > 0 ? Number(unitMultiplier) : 1;
  const baseNow = Number(v);
  const n = baseNow / mul;
  const title = mul === 1
    ? `Seçili depodaki kullanılabilir stok: ${n.toLocaleString('tr-TR')}`
    : `Seçili depodaki kullanılabilir stok: ${n.toLocaleString('tr-TR')} (seçili birim) · ${baseNow.toLocaleString('tr-TR')} taban birim`;
  return (
    <span
      title={title}
      style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: n < 0 ? '#b91c1c' : undefined }}
    >
      {n.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
    </span>
  );
}

export default useStockBalances;
